// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import * as React from 'react'
import classNames from 'classnames'
import CalendarOutlined from '@ant-design/icons/CalendarOutlined'
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import RCPicker from '@kartacam/rc-picker'
import enUS from 'antd/es/date-picker/locale/en_US'
import { getPlaceholder } from '../util'
import { ConfigContext } from 'antd/es/config-provider'
import LocaleReceiver from 'antd/es/locale-provider/LocaleReceiver'
import SizeContext from 'antd/es/config-provider/SizeContext'
import { getTimeProps, Components } from '.'

export default function generatePicker (generateConfig) {
  function getPicker (picker, displayName) {
    class Picker extends React.Component {
      static contextType = ConfigContext

      static displayName

      context

      pickerRef = React.createRef()

      focus = () => {
        if (this.pickerRef.current) {
          this.pickerRef.current.focus()
        }
      }

      blur = () => {
        if (this.pickerRef.current) {
          this.pickerRef.current.blur()
        }
      }

      getDefaultLocale = () => {
        const { locale } = this.props
        const result = {
          ...enUS,
          ...locale
        }
        result.lang = {
          ...result.lang,
          ...(locale || {}).lang
        }
        return result
      }

      renderPicker = locale => {
        const { getPrefixCls, direction, getPopupContainer } = this.context
        const {
          prefixCls: customizePrefixCls,
          getPopupContainer: customizeGetPopupContainer,
          className,
          size: customizeSize,
          bordered = true,
          placeholder,
          ...restProps
        } = this.props
        const { format, showTime } = this.props
        const prefixCls = getPrefixCls('picker', customizePrefixCls)

        const additionalProps = {
          showToday: true
        }

        let additionalOverrideProps = {}
        if (picker) {
          additionalOverrideProps.picker = picker
        }
        const mergedPicker = picker || this.props.picker

        additionalOverrideProps = {
          ...additionalOverrideProps,
          ...(showTime ? getTimeProps({ format, picker: mergedPicker, ...showTime }) : {}),
          ...(mergedPicker === 'time' ? getTimeProps({ format, ...this.props, picker: mergedPicker }) : {})
        }

        return (
          <SizeContext.Consumer>
            {size => {
              const mergedSize = customizeSize || size

              return (
                <RCPicker
                  ref={this.pickerRef}
                  placeholder={getPlaceholder(mergedPicker, locale, placeholder)}
                  suffixIcon={mergedPicker === 'time' ? <ClockCircleOutlined /> : <CalendarOutlined />}
                  clearIcon={<CloseCircleFilled />}
                  allowClear
                  transitionName='slide-up'
                  {...additionalProps}
                  {...restProps}
                  {...additionalOverrideProps}
                  locale={locale.lang}
                  className={classNames(className, {
                    [`${prefixCls}-${mergedSize}`]: mergedSize,
                    [`${prefixCls}-borderless`]: !bordered
                  })}
                  prefixCls={prefixCls}
                  getPopupContainer={customizeGetPopupContainer || getPopupContainer}
                  generateConfig={generateConfig}
                  prevIcon={<span className={`${prefixCls}-prev-icon`} />}
                  nextIcon={<span className={`${prefixCls}-next-icon`} />}
                  superPrevIcon={<span className={`${prefixCls}-super-prev-icon`} />}
                  superNextIcon={<span className={`${prefixCls}-super-next-icon`} />}
                  components={Components}
                  direction={direction}
                />
              )
            }}
          </SizeContext.Consumer>
        )
      }

      render () {
        return (
          <LocaleReceiver componentName='DatePicker' defaultLocale={this.getDefaultLocale}>
            {this.renderPicker}
          </LocaleReceiver>
        )
      }
    }

    if (displayName) {
      Picker.displayName = displayName
    }

    return Picker
  }

  const DatePicker = getPicker()
  const WeekPicker = getPicker('week', 'WeekPicker')
  const MonthPicker = getPicker('month', 'MonthPicker')
  const YearPicker = getPicker('year', 'YearPicker')
  const TimePicker = getPicker('time', 'TimePicker')
  const QuarterPicker = getPicker('quarter', 'QuarterPicker')

  return { DatePicker, WeekPicker, MonthPicker, YearPicker, TimePicker, QuarterPicker }
}
